import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserRole, hasPermission } from '../utils/roleUtils';
import { API_ENDPOINTS } from '../config/apiRouting';
import './LocationOptions.css';

interface LocationConfig {
  storeId: string;
  storeName: string;
  // Payment Processing
  paymentProcessor: string;
  paymentProcessorKeys: {
    merchantId?: string;
    secretKey?: string;
    publicKey?: string;
    // Priv8-specific fields
    priv8MerchantId?: string;
    priv8ApiKey?: string;
    priv8SecretKey?: string;
    priv8StoreId?: string;
  };
  // Social Media Integration
  googleCompanyId?: string;
  googleClientId?: string;
  googleClientSecret?: string;
  facebookCompanyId?: string;
  facebookAppId?: string;
  facebookAppSecret?: string;
  facebookAccessToken?: string;
  instagramCompanyId?: string;
  instagramApiKey?: string;
  instagramAccessToken?: string;
  tiktokCompanyId?: string;
  tiktokApiKey?: string;
  tiktokAccessToken?: string;
  // Third-party APIs
  apiKeys: {
    resy?: string;
    uberEats?: string;
    openTable?: string;
    chowly?: string;
    grubHub?: string;
    doorDash?: string;
    postmates?: string;
  };
  // Device Settings
  screenTimeoutMinutes: number;
  allowServerStayLoggedIn: boolean;
  // KDS (Kitchen Display System) Settings
  kdsTimeColors: {
    greenMaxMinutes: number;
    yellowMaxMinutes: number;
    redMinMinutes: number;
  };
  // Bar Tab Settings
  autoCloseBarTabs: boolean;
  autoGratuityPercent: number;
  // Employee Settings
  employeePinLength: number;
  clockInGracePeriodMinutes: number;
  clockOutGracePeriodMinutes: number;
  // Waitlist Mode
  hostPin?: string;
  // Branding
  logoUrl?: string;
  // Store Hours
  storeHours: {
    monday: { open: string; close: string; closed: boolean };
    tuesday: { open: string; close: string; closed: boolean };
    wednesday: { open: string; close: string; closed: boolean };
    thursday: { open: string; close: string; closed: boolean };
    friday: { open: string; close: string; closed: boolean };
    saturday: { open: string; close: string; closed: boolean };
    sunday: { open: string; close: string; closed: boolean };
  };
  // End of Day
  automaticEndOfDayTime: string;
}

const LocationOptions: React.FC = () => {
  const { user, session } = useAuth();
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [stores, setStores] = useState<any[]>([]);
  const [config, setConfig] = useState<LocationConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('payment');

  const userRole = getUserRole(user, session);
  const canEditSensitiveSettings = hasPermission(user, 'canManageRoles', session);
  const canEditStoreHours = hasPermission(user, 'canManageUsers', session) || userRole === 'manager';

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadStoreConfig();
    }
  }, [selectedStore]);

  const loadStores = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.stores(), {
        headers: {
          'Authorization': `Bearer ${session?.getIdToken().getJwtToken() || ''}`
        }
      });
      const data = await response.json();
      // Handle both wrapped and direct responses
      const storeList = Array.isArray(data) ? data : (data.data || []);
      setStores(storeList);
      if (storeList.length > 0) {
        setSelectedStore(storeList[0].storeId);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadStoreConfig = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.store(selectedStore)}/config`, {
        headers: {
          'Authorization': `Bearer ${session?.getIdToken().getJwtToken() || ''}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const defaults = getDefaultConfig();
        const merged = data.config
          ? {
              ...defaults,
              ...data.config,
              kdsTimeColors: { ...defaults.kdsTimeColors, ...(data.config.kdsTimeColors || {}) },
              storeHours: { ...defaults.storeHours, ...(data.config.storeHours || {}) },
              paymentProcessorKeys: { ...defaults.paymentProcessorKeys, ...(data.config.paymentProcessorKeys || {}) },
              apiKeys: { ...defaults.apiKeys, ...(data.config.apiKeys || {}) },
            }
          : defaults;
        setConfig(merged);
      } else {
        setConfig(getDefaultConfig());
      }
    } catch (error) {
      console.error('Error loading store config:', error);
      setConfig(getDefaultConfig());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultConfig = (): LocationConfig => ({
    storeId: selectedStore,
    storeName: stores.find(s => s.storeId === selectedStore)?.storeName || '',
    paymentProcessor: 'square',
    paymentProcessorKeys: {},
    apiKeys: {},
    screenTimeoutMinutes: 30,
    allowServerStayLoggedIn: false,
    kdsTimeColors: {
      greenMaxMinutes: 8,
      yellowMaxMinutes: 14,
      redMinMinutes: 15
    },
    autoCloseBarTabs: false,
    autoGratuityPercent: 18,
    employeePinLength: 4,
    clockInGracePeriodMinutes: 5,
    clockOutGracePeriodMinutes: 5,
    storeHours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '09:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '21:00', closed: false }
    },
    automaticEndOfDayTime: '04:00'
  });

  const handleSave = async () => {
    if (!config) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.store(selectedStore)}/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.getIdToken().getJwtToken() || ''}`
        },
        body: JSON.stringify({ config })
      });
      
      if (response.ok) {
        alert('Configuration saved successfully!');
      } else {
        alert('Error saving configuration. Please try again.');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field: string, value: any) => {
    if (!config) return;
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setConfig({
        ...config,
        [parent]: {
          ...(config as any)[parent],
          [child]: value
        }
      });
    } else {
      setConfig({
        ...config,
        [field]: value
      });
    }
  };

  const updateNestedConfig = (parent: string, child: string, subChild: string, value: any) => {
    if (!config) return;
    
    setConfig({
      ...config,
      [parent]: {
        ...(config as any)[parent],
        [child]: {
          ...((config as any)[parent][child] || {}),
          [subChild]: value
        }
      }
    });
  };

  const updatePaymentProcessorKey = (key: string, value: any) => {
    if (!config) return;
    
    setConfig({
      ...config,
      paymentProcessorKeys: {
        ...(config.paymentProcessorKeys || {}),
        [key]: value
      }
    });
  };

  const updateApiKey = (key: string, value: any) => {
    if (!config) return;
    
    setConfig({
      ...config,
      apiKeys: {
        ...(config.apiKeys || {}),
        [key]: value
      }
    });
  };

  if (!config) {
    return <div className="loading">Loading location configuration...</div>;
  }

  return (
    <div className="location-options">
      <div className="location-options-header">
        <h1>Location Options</h1>
        <div className="store-selector">
          <label htmlFor="store-select">Select Store:</label>
          <select
            id="store-select"
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
          >
            {stores.map(store => (
              <option key={store.storeId} value={store.storeId}>
                {store.storeName} ({store.storeCode})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="config-tabs">
        <button 
          className={activeTab === 'payment' ? 'active' : ''}
          onClick={() => setActiveTab('payment')}
        >
          Payment & APIs
        </button>
        <button 
          className={activeTab === 'device' ? 'active' : ''}
          onClick={() => setActiveTab('device')}
        >
          Device Settings
        </button>
        <button 
          className={activeTab === 'operations' ? 'active' : ''}
          onClick={() => setActiveTab('operations')}
        >
          Operations
        </button>
        <button 
          className={activeTab === 'hours' ? 'active' : ''}
          onClick={() => setActiveTab('hours')}
        >
          Store Hours
        </button>
        <button 
          className={activeTab === 'branding' ? 'active' : ''}
          onClick={() => setActiveTab('branding')}
        >
          Branding
        </button>
      </div>

      <div className="config-content">
        {activeTab === 'payment' && (
          <div className="config-section">
            <h3>Payment Processing</h3>
            <div className="form-group">
              <label>Payment Processor:</label>
              <select 
                value={config.paymentProcessor}
                onChange={(e) => updateConfig('paymentProcessor', e.target.value)}
                disabled={!canEditSensitiveSettings}
              >
                <option value="square">Square</option>
                <option value="stripe">Stripe</option>
                <option value="clover">Clover</option>
                <option value="priv8">Priv8</option>
              </select>
            </div>

            {canEditSensitiveSettings && config.paymentProcessor !== 'priv8' && (
              <>
                <div className="form-group">
                  <label>Merchant ID:</label>
                  <input
                    type="password"
                    value={config.paymentProcessorKeys?.merchantId || ''}
                    onChange={(e) => updatePaymentProcessorKey('merchantId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Secret Key:</label>
                  <input
                    type="password"
                    value={config.paymentProcessorKeys?.secretKey || ''}
                    onChange={(e) => updatePaymentProcessorKey('secretKey', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Public Key:</label>
                  <input
                    type="text"
                    value={config.paymentProcessorKeys?.publicKey || ''}
                    onChange={(e) => updatePaymentProcessorKey('publicKey', e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Priv8 Payment Processor Configuration */}
            {canEditSensitiveSettings && config.paymentProcessor === 'priv8' && (
              <>
                <div className="form-group">
                  <label>Priv8 Merchant ID:</label>
                  <input
                    type="text"
                    value={config.paymentProcessorKeys?.priv8MerchantId || ''}
                    onChange={(e) => updatePaymentProcessorKey('priv8MerchantId', e.target.value)}
                    placeholder="merchant_bigsystemone_nor001"
                  />
                </div>
                <div className="form-group">
                  <label>Priv8 API Key:</label>
                  <input
                    type="password"
                    value={config.paymentProcessorKeys?.priv8ApiKey || ''}
                    onChange={(e) => updatePaymentProcessorKey('priv8ApiKey', e.target.value)}
                    placeholder="pk_live_... or pk_test_..."
                  />
                </div>
                <div className="form-group">
                  <label>Priv8 Secret Key:</label>
                  <input
                    type="password"
                    value={config.paymentProcessorKeys?.priv8SecretKey || ''}
                    onChange={(e) => updatePaymentProcessorKey('priv8SecretKey', e.target.value)}
                    placeholder="sk_live_... or sk_test_..."
                  />
                </div>
                <div className="form-group">
                  <label>Priv8 Store ID:</label>
                  <input
                    type="text"
                    value={config.paymentProcessorKeys?.priv8StoreId || ''}
                    onChange={(e) => updatePaymentProcessorKey('priv8StoreId', e.target.value)}
                    placeholder="store_nor001"
                  />
                </div>
                <div className="form-group">
                  <small className="text-muted">
                    <strong>Priv8 Setup Instructions:</strong><br/>
                    1. Contact your Priv8 account manager for API credentials<br/>
                    2. For production: Use pk_live_... and sk_live_... keys<br/>
                    3. For testing: Use pk_test_... and sk_test_... keys<br/>
                    4. Merchant ID format: merchant_[company]_[location]<br/>
                    5. Store ID format: store_[location_identifier]
                  </small>
                </div>
              </>
            )}

            <h3>Social Media Integration</h3>
            {canEditSensitiveSettings && (
              <>
                <div className="form-group">
                  <label>Google Company ID:</label>
                  <input
                    type="text"
                    value={config.googleCompanyId || ''}
                    onChange={(e) => updateConfig('googleCompanyId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Google Client ID:</label>
                  <input
                    type="text"
                    value={config.googleClientId || ''}
                    onChange={(e) => updateConfig('googleClientId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Google Client Secret:</label>
                  <input
                    type="password"
                    value={config.googleClientSecret || ''}
                    onChange={(e) => updateConfig('googleClientSecret', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Facebook Page ID:</label>
                  <input
                    type="text"
                    value={config.facebookCompanyId || ''}
                    onChange={(e) => updateConfig('facebookCompanyId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Facebook App ID:</label>
                  <input
                    type="text"
                    value={config.facebookAppId || ''}
                    onChange={(e) => updateConfig('facebookAppId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Facebook App Secret:</label>
                  <input
                    type="password"
                    value={config.facebookAppSecret || ''}
                    onChange={(e) => updateConfig('facebookAppSecret', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Facebook Access Token:</label>
                  <input
                    type="password"
                    value={config.facebookAccessToken || ''}
                    onChange={(e) => updateConfig('facebookAccessToken', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Instagram Account ID:</label>
                  <input
                    type="text"
                    value={config.instagramCompanyId || ''}
                    onChange={(e) => updateConfig('instagramCompanyId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Instagram API Key:</label>
                  <input
                    type="password"
                    value={config.instagramApiKey || ''}
                    onChange={(e) => updateConfig('instagramApiKey', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Instagram Access Token:</label>
                  <input
                    type="password"
                    value={config.instagramAccessToken || ''}
                    onChange={(e) => updateConfig('instagramAccessToken', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>TikTok Account ID:</label>
                  <input
                    type="text"
                    value={config.tiktokCompanyId || ''}
                    onChange={(e) => updateConfig('tiktokCompanyId', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>TikTok API Key:</label>
                  <input
                    type="password"
                    value={config.tiktokApiKey || ''}
                    onChange={(e) => updateConfig('tiktokApiKey', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>TikTok Access Token:</label>
                  <input
                    type="password"
                    value={config.tiktokAccessToken || ''}
                    onChange={(e) => updateConfig('tiktokAccessToken', e.target.value)}
                  />
                </div>
              </>
            )}

            <h3>Third-Party API Keys</h3>
            {canEditSensitiveSettings && (
              <>
                <div className="form-group">
                  <label>Resy API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.resy || ''}
                    onChange={(e) => updateApiKey('resy', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>UberEats API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.uberEats || ''}
                    onChange={(e) => updateApiKey('uberEats', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>OpenTable API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.openTable || ''}
                    onChange={(e) => updateApiKey('openTable', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Chowly API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.chowly || ''}
                    onChange={(e) => updateApiKey('chowly', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>GrubHub API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.grubHub || ''}
                    onChange={(e) => updateApiKey('grubHub', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>DoorDash API Key:</label>
                  <input
                    type="password"
                    value={config.apiKeys?.doorDash || ''}
                    onChange={(e) => updateApiKey('doorDash', e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'device' && (
          <div className="config-section">
            <h3>Device & POS Settings</h3>
            <div className="form-group">
              <label>Screen Timeout (minutes):</label>
              <input
                type="number"
                min="5"
                max="120"
                value={config.screenTimeoutMinutes}
                onChange={(e) => updateConfig('screenTimeoutMinutes', parseInt(e.target.value))}
              />
            </div>
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="stay-logged-in"
                checked={config.allowServerStayLoggedIn}
                onChange={(e) => updateConfig('allowServerStayLoggedIn', e.target.checked)}
              />
              <label htmlFor="stay-logged-in">Allow servers to stay logged in to device</label>
            </div>

            <h3>KDS Time Colors</h3>
            <div className="form-group">
              <label>Green (1-X minutes):</label>
              <input
                type="number"
                min="1"
                max="30"
                value={config.kdsTimeColors.greenMaxMinutes}
                onChange={(e) => updateNestedConfig('kdsTimeColors', 'greenMaxMinutes', '', parseInt(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Yellow (X-Y minutes):</label>
              <input
                type="number"
                min="5"
                max="45"
                value={config.kdsTimeColors.yellowMaxMinutes}
                onChange={(e) => updateNestedConfig('kdsTimeColors', 'yellowMaxMinutes', '', parseInt(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Red (Y+ minutes):</label>
              <input
                type="number"
                min="10"
                max="60"
                value={config.kdsTimeColors.redMinMinutes}
                onChange={(e) => updateNestedConfig('kdsTimeColors', 'redMinMinutes', '', parseInt(e.target.value))}
              />
            </div>
          </div>
        )}

        {activeTab === 'operations' && (
          <div className="config-section">
            <h3>Bar Tab Settings</h3>
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="auto-close-bar-tabs"
                checked={config.autoCloseBarTabs}
                onChange={(e) => updateConfig('autoCloseBarTabs', e.target.checked)}
              />
              <label htmlFor="auto-close-bar-tabs">Auto-close bar tabs at end of day</label>
            </div>
            <div className="form-group">
              <label>Auto-gratuity percentage (%):</label>
              <input
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={config.autoGratuityPercent}
                onChange={(e) => updateConfig('autoGratuityPercent', parseFloat(e.target.value))}
              />
            </div>

            <h3>Employee Clock In/Out Settings</h3>
            <div className="form-group">
              <label>Employee PIN length (digits):</label>
              <input
                type="number"
                min="4"
                max="8"
                value={config.employeePinLength || 4}
                onChange={(e) => updateConfig('employeePinLength', parseInt(e.target.value))}
              />
              <small>Number of digits required for employee PINs (4-8 digits)</small>
            </div>

            <h3>Waitlist Mode</h3>
            <div className="form-group">
              <label>Host PIN:</label>
              <input
                type="text"
                maxLength={8}
                placeholder="e.g. 0000"
                value={config.hostPin || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  updateConfig('hostPin', val || undefined);
                }}
              />
              <small>Shared PIN for hosts to access Waitlist Mode on the POS tablet. Leave blank to disable.</small>
            </div>

            <div className="form-group">
              <label>Clock-in grace period (minutes before scheduled time):</label>
              <input
                type="number"
                min="0"
                max="30"
                value={config.clockInGracePeriodMinutes}
                onChange={(e) => updateConfig('clockInGracePeriodMinutes', parseInt(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Clock-out grace period (minutes after scheduled time):</label>
              <input
                type="number"
                min="0"
                max="60"
                value={config.clockOutGracePeriodMinutes}
                onChange={(e) => updateConfig('clockOutGracePeriodMinutes', parseInt(e.target.value))}
              />
            </div>

            <h3>End of Day Settings</h3>
            <div className="form-group">
              <label>Automatic end of day time (24-hour format):</label>
              <input
                type="time"
                value={config.automaticEndOfDayTime}
                onChange={(e) => updateConfig('automaticEndOfDayTime', e.target.value)}
              />
              <small>Time when open checks are moved to next business day</small>
            </div>
          </div>
        )}

        {activeTab === 'hours' && (
          <div className="config-section">
            <h3>Store Hours</h3>
            {Object.entries(config.storeHours).map(([day, hours]) => (
              <div key={day} className="hours-row">
                <div className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                <div className="hours-controls">
                  <input
                    type="checkbox"
                    id={`${day}-closed`}
                    checked={hours.closed}
                    onChange={(e) => updateNestedConfig('storeHours', day, 'closed', e.target.checked)}
                    disabled={!canEditStoreHours}
                  />
                  <label htmlFor={`${day}-closed`}>Closed</label>
                  
                  {!hours.closed && (
                    <>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => updateNestedConfig('storeHours', day, 'open', e.target.value)}
                        disabled={!canEditStoreHours}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => updateNestedConfig('storeHours', day, 'close', e.target.value)}
                        disabled={!canEditStoreHours}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="config-section">
            <h3>Logo & Branding</h3>
            <div className="form-group">
              <label>Logo URL:</label>
              <input
                type="url"
                value={config.logoUrl || ''}
                onChange={(e) => updateConfig('logoUrl', e.target.value)}
                placeholder="https://example.com/logo.png"
              />
              <small>Used in printer headers, check receipts, and online ordering</small>
            </div>
            {config.logoUrl && (
              <div className="logo-preview">
                <img src={config.logoUrl} alt="Store Logo" style={{ maxWidth: '200px', maxHeight: '100px' }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="config-actions">
        <button 
          className="save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {!canEditSensitiveSettings && (
        <div className="access-notice">
          <p>[WARNING] Some sensitive settings (API keys, payment processor settings) are hidden. Contact an administrator to modify these settings.</p>
        </div>
      )}
    </div>
  );
};

export default LocationOptions;